import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { ShieldCheck, KeyRound } from "lucide-react";

/**
 * Emergency operator-only password rescue page.
 *
 * No login required. Reaches the public-but-secret-gated endpoint
 * /api/auth/operator-reset, which validates the supplied operator_secret
 * (the JWT_SECRET env var, only visible to whoever has access to the
 * deployment's env file) before changing the target user's password.
 *
 * Use when:
 *   - The admin email is a placeholder/unread inbox so forgot-password
 *     can't deliver
 *   - You're locked out and can't wait 15 min
 *   - You need to recover a driver/customer in a hurry
 */
export default function AdminRescue() {
  const [operatorSecret, setOperatorSecret] = useState("");
  const [email, setEmail] = useState("admin@linkpwa.app");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!operatorSecret || !email || !newPassword) {
      toast.error("All fields required.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/operator-reset`, {
        operator_secret: operatorSecret,
        email,
        new_password: newPassword,
      });
      toast.success(`Reset for ${data.user?.email} — you can sign in now.`, { duration: 8000 });
      setOperatorSecret("");
      setNewPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30">
            <ShieldCheck className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-amber-300" style={{ fontFamily: "Outfit, system-ui" }}>
              Operator Rescue
            </h1>
            <p className="text-xs text-zinc-500">Reset a password using the deployment&apos;s JWT_SECRET.</p>
          </div>
        </div>

        <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4 text-xs text-amber-200/90 leading-relaxed">
          <p className="font-bold text-amber-300 mb-1">Only the deployment owner should see this page.</p>
          <p>
            The operator secret is the <code className="bg-black/40 px-1.5 py-0.5 rounded text-amber-300">JWT_SECRET</code> env var
            from <code className="bg-black/40 px-1.5 py-0.5 rounded text-amber-300">backend/.env</code>. Without it nothing here works.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-zinc-400">Operator secret (JWT_SECRET)</Label>
            <Input
              type="password"
              autoComplete="off"
              value={operatorSecret}
              onChange={(e) => setOperatorSecret(e.target.value)}
              placeholder="Paste JWT_SECRET from your .env"
              className="bg-zinc-900 border-white/10 text-white font-mono text-sm"
              data-testid="rescue-secret"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Account email to reset</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-zinc-900 border-white/10 text-white text-sm"
              data-testid="rescue-email"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">New password</Label>
            <Input
              type="text"
              autoComplete="off"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="bg-zinc-900 border-white/10 text-white text-sm"
              data-testid="rescue-new-pw"
            />
          </div>
        </div>

        <Button
          onClick={submit}
          disabled={busy}
          className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-xl disabled:opacity-40"
          data-testid="rescue-submit"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          {busy ? "Resetting…" : "Reset password"}
        </Button>

        <p className="text-[10px] text-zinc-600 text-center">
          After success, head to <a href="/login" className="underline">/login</a> and sign in with the new password.
        </p>
      </div>
    </div>
  );
}
